import { useContext, useMemo } from 'react';
import { COLUMN_SIZE } from '@kizen/kds/Grid';
import { merge } from '@kizen/kds/util';
import { useTranslation } from 'react-i18next';
import { RecordLayoutPreviewProps } from './types';
import { getWidthClassName } from '../Builder/util';
import FullWidthTeam from './recordLayoutPreview/team/team_block_full.svg?react';
import LargeTeam from './recordLayoutPreview/team/team_block_large.svg?react';
import MediumTeam from './recordLayoutPreview/team/team_block_medium.svg?react';
import SmallTeam from './recordLayoutPreview/team/team_block_small.svg?react';
import FullWidthActionBlock from './recordLayoutPreview/action/action_block_full.svg?react';
import LargeActionBlock from './recordLayoutPreview/action/action_block_large.svg?react';
import MediumActionBlock from './recordLayoutPreview/action/action_block_medium.svg?react';
import SmallActionBlock from './recordLayoutPreview/action/action_block_small.svg?react';
import FullWidthLeadInfo from './recordLayoutPreview/leadInfo/lead_info_block_full.svg?react';
import LargeLeadInfo from './recordLayoutPreview/leadInfo/lead_info_block_large.svg?react';
import MediumLeadInfo from './recordLayoutPreview/leadInfo/lead_info_block_medium.svg?react';
import SmallLeadInfo from './recordLayoutPreview/leadInfo/lead_info_block_small.svg?react';
import FullWidthRelatedObjects from './recordLayoutPreview/relatedObjects/objects_block_full.svg?react';
import LargeRelatedObjects from './recordLayoutPreview/relatedObjects/objects_block_large.svg?react';
import MediumRelatedObjects from './recordLayoutPreview/relatedObjects/objects_block_medium.svg?react';
import SmallRelatedObjects from './recordLayoutPreview/relatedObjects/objects_block_small.svg?react';
import FullWidthRelatedPipelines from './recordLayoutPreview/relatedPipelines/pipelines_block_full.svg?react';
import LargeRelatedPipelines from './recordLayoutPreview/relatedPipelines/pipelines_block_large.svg?react';
import MediumRelatedPipelines from './recordLayoutPreview/relatedPipelines/pipelines_block_medium.svg?react';
import SmallRelatedPipelines from './recordLayoutPreview/relatedPipelines/pipelines_block_small.svg?react';
import FullWidthTimeline from './recordLayoutPreview/timeline/timeline_block_full.svg?react';
import LargeTimeline from './recordLayoutPreview/timeline/timeline_block_large.svg?react';
import MediumTimeline from './recordLayoutPreview/timeline/timeline_block_medium.svg?react';
import SmallTimeline from './recordLayoutPreview/timeline/timeline_block_small.svg?react';
import FullWidthFieldCategories from './recordLayoutPreview/fieldCategories/field_categories_block_full.svg?react';
import LargeFieldCategories from './recordLayoutPreview/fieldCategories/field_categories_block_large.svg?react';
import MediumFieldCategories from './recordLayoutPreview/fieldCategories/field_categories_block_medium.svg?react';
import SmallFieldCategories from './recordLayoutPreview/fieldCategories/field_categories_block_small.svg?react';
import FullWidthCustomContent from './recordLayoutPreview/customContent/custom_content_block_full.svg?react';
import LargeCustomContent from './recordLayoutPreview/customContent/custom_content_block_large.svg?react';
import MediumCustomContent from './recordLayoutPreview/customContent/custom_content_block_medium.svg?react';
import SmallCustomContent from './recordLayoutPreview/customContent/custom_content_block_small.svg?react';
import { PluginContext } from 'ts-components/Plugins/context';
import { PluginBlockPreview } from './PluginBlockPreview';

const getSvgForBlock = (type: string, size: COLUMN_SIZE) => {
  switch (type) {
    case 'team_and_activities': {
      switch (size) {
        case COLUMN_SIZE.FULL_WIDTH:
          return FullWidthTeam;
        case COLUMN_SIZE.TWO_THIRD_WIDTH:
          return LargeTeam;
        case COLUMN_SIZE.HALF_WIDTH:
          return MediumTeam;
        case COLUMN_SIZE.THIRD_WIDTH:
          return SmallTeam;
        default:
          return null;
      }
    }
    case 'action_block': {
      switch (size) {
        case COLUMN_SIZE.FULL_WIDTH:
          return FullWidthActionBlock;
        case COLUMN_SIZE.TWO_THIRD_WIDTH:
          return LargeActionBlock;
        case COLUMN_SIZE.HALF_WIDTH:
          return MediumActionBlock;
        case COLUMN_SIZE.THIRD_WIDTH:
          return SmallActionBlock;
        default:
          return null;
      }
    }
    case 'lead_sources': {
      switch (size) {
        case COLUMN_SIZE.FULL_WIDTH:
          return FullWidthLeadInfo;
        case COLUMN_SIZE.TWO_THIRD_WIDTH:
          return LargeLeadInfo;
        case COLUMN_SIZE.HALF_WIDTH:
          return MediumLeadInfo;
        case COLUMN_SIZE.THIRD_WIDTH:
          return SmallLeadInfo;
        default:
          return null;
      }
    }
    case 'related_object_fields': {
      switch (size) {
        case COLUMN_SIZE.FULL_WIDTH:
          return FullWidthRelatedObjects;
        case COLUMN_SIZE.TWO_THIRD_WIDTH:
          return LargeRelatedObjects;
        case COLUMN_SIZE.HALF_WIDTH:
          return MediumRelatedObjects;
        case COLUMN_SIZE.THIRD_WIDTH:
          return SmallRelatedObjects;
        default:
          return null;
      }
    }
    case 'related_pipelines': {
      switch (size) {
        case COLUMN_SIZE.FULL_WIDTH:
          return FullWidthRelatedPipelines;
        case COLUMN_SIZE.TWO_THIRD_WIDTH:
          return LargeRelatedPipelines;
        case COLUMN_SIZE.HALF_WIDTH:
          return MediumRelatedPipelines;
        case COLUMN_SIZE.THIRD_WIDTH:
          return SmallRelatedPipelines;
        default:
          return null;
      }
    }
    case 'timeline': {
      switch (size) {
        case COLUMN_SIZE.FULL_WIDTH:
          return FullWidthTimeline;
        case COLUMN_SIZE.TWO_THIRD_WIDTH:
          return LargeTimeline;
        case COLUMN_SIZE.HALF_WIDTH:
          return MediumTimeline;
        case COLUMN_SIZE.THIRD_WIDTH:
          return SmallTimeline;
        default:
          return null;
      }
    }
    case 'fields': {
      switch (size) {
        case COLUMN_SIZE.FULL_WIDTH:
          return FullWidthFieldCategories;
        case COLUMN_SIZE.TWO_THIRD_WIDTH:
          return LargeFieldCategories;
        case COLUMN_SIZE.HALF_WIDTH:
          return MediumFieldCategories;
        case COLUMN_SIZE.THIRD_WIDTH:
          return SmallFieldCategories;
        default:
          return null;
      }
    }
    case 'custom_content': {
      switch (size) {
        case COLUMN_SIZE.FULL_WIDTH:
          return FullWidthCustomContent;
        case COLUMN_SIZE.TWO_THIRD_WIDTH:
          return LargeCustomContent;
        case COLUMN_SIZE.HALF_WIDTH:
          return MediumCustomContent;
        case COLUMN_SIZE.THIRD_WIDTH:
          return SmallCustomContent;
        default:
          return null;
      }
    }
    default:
      return null;
  }
};

export const RecordLayoutPreview = (props: RecordLayoutPreviewProps) => {
  const { layout } = props;
  const { t } = useTranslation();
  const { pluginBlocks, businessPluginAppsByApiName } =
    useContext(PluginContext);

  const pluginBlockNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const entry of pluginBlocks) {
      for (const block of entry.blocks) {
        if (block.api_name) {
          map[`${entry.plugin_api_name}:${block.api_name}`] = block.name;
        }
      }
    }
    return map;
  }, [pluginBlocks]);

  return (
    <div className="flex flex-col gap-y-10">
      {layout
        .map((row) => {
          if (!row.columns.some((column) => column.items.length > 0)) {
            return null;
          }

          return (
            <div key={row.id} className="kds grid grid-cols-6 gap-x-spacer-10">
              {row.columns.map((column) => {
                const { width } = column;
                const widthClassName = getWidthClassName(width);

                return (
                  <div
                    key={column.id}
                    className={merge('kds grow flex', widthClassName)}
                  >
                    <div className="kds min-w-0 w-full flex flex-col gap-y-spacer-10">
                      {column.items.map((item) => {
                        if (item.hidden) {
                          return null;
                        }

                        if (item.type === 'plugin_block') {
                          const { pluginApiName, blockApiName } =
                            item.metadata ?? {};
                          if (
                            businessPluginAppsByApiName[pluginApiName]?.disabled
                          ) {
                            return null;
                          }
                          const blockName =
                            pluginBlockNameMap[
                              `${pluginApiName}:${blockApiName}`
                            ] ?? t('Plugin Block');
                          return (
                            <div
                              key={item.id}
                              className="select-none pointer-events-none"
                            >
                              <PluginBlockPreview
                                blockName={blockName}
                                size={column.width}
                              />
                            </div>
                          );
                        }

                        const SvgForBlock = getSvgForBlock(
                          item.type,
                          column.width
                        );

                        return SvgForBlock ? (
                          <div
                            key={item.id}
                            className="select-none pointer-events-none"
                            aria-label={t('Block Preview')}
                          >
                            <SvgForBlock className="w-full" />
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })
        .filter(Boolean)}
    </div>
  );
};
